/**
 * otaStore — OTA 업데이트 UI 상태 (non-React liveUpdate → React UpdatePrompt 브릿지).
 *
 * liveUpdate.ts(네이티브 계층)가 BE version-policy 결과에 따라 이 store 를 갱신하고,
 * UpdatePrompt 컴포넌트가 구독해 안내/강제 UI 를 렌더한다.
 *
 * 설계: docs/84_OTA_LIVE_UPDATE_DESIGN.md §5
 */
import { create } from "zustand";

/** UI 가 표시할 OTA 상태. */
export type OtaPromptKind =
  | "none"
  | "prompt" // 선택 업데이트 — 지금/나중에
  | "mandatory" // 강제 업데이트 — 닫기 불가
  | "store"; // 네이티브 셸이 낡아 스토어 업데이트 필요

export interface OtaPromptState {
  kind: OtaPromptKind;
  /** 적용 대기 중인 capgo 번들 id (download 완료 후). store kind 에서는 null. */
  bundleId: string | null;
  version: string | null;
  releaseNotes: string | null;
  /** store kind 전용 — 강제(차단) 여부. true 면 닫기 불가 게이트. */
  storeMandatory: boolean;
  /** store kind 전용 — 커스텀 안내 메시지(없으면 기본 문구). */
  storeMessage: string | null;
  /** 적용(set) 진행 중 — 버튼 비활성/스피너용. */
  applying: boolean;
}

interface OtaStoreActions {
  showPrompt: (bundleId: string, version: string, releaseNotes: string | null) => void;
  showMandatory: (bundleId: string, version: string, releaseNotes: string | null) => void;
  showStoreUpdate: (mandatory: boolean, version: string | null, message: string | null) => void;
  setApplying: (applying: boolean) => void;
  dismiss: () => void;
}

const initialState: OtaPromptState = {
  kind: "none",
  bundleId: null,
  version: null,
  releaseNotes: null,
  storeMandatory: false,
  storeMessage: null,
  applying: false,
};

export const useOtaStore = create<OtaPromptState & OtaStoreActions>((set) => ({
  ...initialState,

  showPrompt: (bundleId, version, releaseNotes) =>
    set({ kind: "prompt", bundleId, version, releaseNotes, applying: false }),

  showMandatory: (bundleId, version, releaseNotes) =>
    set({ kind: "mandatory", bundleId, version, releaseNotes, applying: false }),

  showStoreUpdate: (mandatory, version, message) =>
    set({
      kind: "store",
      bundleId: null,
      version,
      releaseNotes: null,
      storeMandatory: mandatory,
      storeMessage: message,
      applying: false,
    }),

  setApplying: (applying) => set({ applying }),

  // mandatory 는 사용자가 닫을 수 없음 — dismiss 는 prompt/store 에서만 호출.
  dismiss: () => set(initialState),
}));
